import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

import api from '../../../../services/api';

// Onda 3 · aba "Torre de Controle": rotas AO VIVO de hoje com ETA por
// parada (OSRM). Atualiza sozinha a cada 60s — é a visão do "agora",
// então o seletor de período do painel não se aplica aqui.

interface ITowerRoute {
  routeId: number;
  routeName: string;
  status: string;
  driverName: string;
  lastSeenAt: string | null;
  stopsDone: number;
  stopsTotal: number;
  nextStop: { customer: string; city: string } | null;
  etaNextStop: string | null;
  etaLastStop: string | null;
  delayRisk: boolean;
  etaStatus:
    | 'ok'
    | 'risco'
    | 'concluida'
    | 'sem-gps'
    | 'sem-coordenadas'
    | 'roteador-indisponivel';
}

const hora = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const vistoHa = (iso: string | null) => {
  if (!iso) return null;
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}`;
};

const STATUS_CHIP: Record<
  ITowerRoute['etaStatus'],
  { label: string; bg: string; fg: string }
> = {
  ok: { label: 'No prazo', bg: '#EAF5E8', fg: '#2E7D32' },
  risco: { label: 'Risco de atraso', bg: '#FAE4E4', fg: '#C43D3D' },
  concluida: { label: 'Paradas concluídas', bg: '#EDF1F5', fg: '#5A6B7A' },
  'sem-gps': { label: 'Sem GPS', bg: '#FBF0DC', fg: '#9A6A0B' },
  'sem-coordenadas': {
    label: 'Endereços sem coordenada',
    bg: '#FBF0DC',
    fg: '#9A6A0B',
  },
  'roteador-indisponivel': {
    label: 'Roteador indisponível',
    bg: '#EDF1F5',
    fg: '#5A6B7A',
  },
};

export const ControlTowerTab = () => {
  const [data, setData] = useState<{ routes: ITowerRoute[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get('/summarizers/control-tower');
      setData(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 60_000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={8}>
        <CircularProgress />
      </Box>
    );
  }

  const routes = data?.routes || [];

  return (
    <Box>
      <Box display="flex" alignItems="center" gridGap={8} mb={1}>
        <FiberManualRecordIcon style={{ color: '#2E7D32', fontSize: 12 }} />
        <Typography variant="body2" color="textSecondary">
          Rotas de hoje em andamento — ETA recalculado a cada minuto a partir
          da posição do motorista. Risco = término previsto após as 18h.
        </Typography>
      </Box>

      {routes.length === 0 ? (
        <Paper style={{ padding: 32, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Nenhuma rota em andamento agora.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rota</TableCell>
                <TableCell>Motorista</TableCell>
                <TableCell align="center">Visto há</TableCell>
                <TableCell align="center">Paradas</TableCell>
                <TableCell>Próxima parada</TableCell>
                <TableCell align="center">ETA próxima</TableCell>
                <TableCell align="center">Término previsto</TableCell>
                <TableCell align="center">Situação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {routes.map((r) => {
                const chip = STATUS_CHIP[r.etaStatus] || STATUS_CHIP.ok;
                const visto = vistoHa(r.lastSeenAt);
                return (
                  <TableRow key={r.routeId} hover>
                    <TableCell>
                      <b>{r.routeName}</b>
                    </TableCell>
                    <TableCell>{r.driverName}</TableCell>
                    <TableCell align="center">
                      {visto ? (
                        visto
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {r.stopsDone}/{r.stopsTotal}
                    </TableCell>
                    <TableCell>
                      {r.nextStop ? (
                        <Tooltip title={r.nextStop.customer}>
                          <span>
                            {r.nextStop.customer.slice(0, 28)}
                            {r.nextStop.customer.length > 28 ? '…' : ''}
                            {r.nextStop.city ? ` · ${r.nextStop.city}` : ''}
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </TableCell>
                    <TableCell align="center">{hora(r.etaNextStop)}</TableCell>
                    <TableCell align="center">
                      <b
                        style={{
                          color: r.delayRisk ? '#C43D3D' : undefined,
                        }}
                      >
                        {hora(r.etaLastStop)}
                      </b>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={chip.label}
                        style={{
                          background: chip.bg,
                          color: chip.fg,
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};
